/*
 * The night map behind the Visit hero: a schematic of the country between the M5 and Hollowmoor Ridge,
 * drawn in contour lines.
 *
 * Map units are about 40 metres east to west, with the country south of the observatory drawn at 60 per cent
 * so the whole drive fits the frame. The observatory sits at the origin, x runs east and y runs south, as SVG does.
 *
 * Everything here is deterministic (seeded value noise and plain arithmetic, no trigonometry or exponentials
 * in the terrain), so the server and the browser draw exactly the same lines.
 */

export type MapPoint = {
    x: number
    y: number
}

export type MapView = {
    x: number
    y: number
    w: number
    h: number
}

export type ContourPath = {
    key: string
    d: string
    kind: 'line' | 'index' | 'coast'
    delay: number
    duration: number
}

export type MapLine = {
    key: string
    d: string
    delay: number
    duration: number
}

export type Road = 'A361' | 'A396' | 'Hollowmoor Lane'

export type RouteSample = {
    x: number
    y: number
    minutes: number
    road: Road
}

export const PLACES = {
    observatory: { x: 0, y: 0 },
    wheddonCross: { x: 35, y: 24 },
    dulverton: { x: 25, y: 186 },
    exebridge: { x: 75, y: 226 },
    tiverton: { x: 140, y: 412 },
    junction27: { x: 362, y: 375 },
    wellington: { x: 592, y: 296 },
    taunton: { x: 807, y: 232 },
    minehead: { x: 152, y: -132 },
    porlock: { x: -52, y: -146 },
    exford: { x: -130, y: 33 },
    dunkery: { x: -140, y: -62 }
} satisfies Record<string, MapPoint>

/** Minutes from the M5 at junction 27 to the observatory, as the Getting here copy says. */
export const TRAVEL_MINUTES = 50

/** Stars visible to the eye on a clear night: under Taunton's glow, and up on the ridge. */
export const STARS_IN_TOWN = 300
export const STARS_ON_THE_RIDGE = 4500

/** Sky brightness in magnitudes per square arcsecond: an edge-of-town sky, and the reserve's darkest. */
export const SKY_IN_TOWN = 18.3
export const SKY_ON_THE_RIDGE = 21.6

const smooth01 = (t: number): number => (t <= 0 ? 0 : t >= 1 ? 1 : t * t * (3 - 2 * t))

/* ---------------------------------------------------------------------------------------------- */
/* Seeded value noise                                                                               */
/* ---------------------------------------------------------------------------------------------- */

const hash = (ix: number, iy: number): number => {
    let h = (Math.imul(ix, 374761393) + Math.imul(iy, 668265263) + 1013904223) | 0

    h = Math.imul(h ^ (h >>> 13), 1274126177)
    h ^= h >>> 16

    return (h >>> 0) / 4294967296
}

const valueNoise = (x: number, y: number): number => {
    const ix = Math.floor(x)
    const iy = Math.floor(y)
    const fx = x - ix
    const fy = y - iy
    const ux = fx * fx * (3 - 2 * fx)
    const uy = fy * fy * (3 - 2 * fy)
    const a = hash(ix, iy)
    const b = hash(ix + 1, iy)
    const c = hash(ix, iy + 1)
    const d = hash(ix + 1, iy + 1)
    const top = a + (b - a) * ux
    const bottom = c + (d - c) * ux

    return (top + (bottom - top) * uy) * 2 - 1
}

const fbm = (x: number, y: number): number => (
    valueNoise(x / 240, y / 240)
    + valueNoise(x / 115 + 17.3, y / 115 - 4.1) * 0.5
    + valueNoise(x / 56 - 9.7, y / 56 + 3.3) * 0.12
) / 1.62

/* ---------------------------------------------------------------------------------------------- */
/* Terrain                                                                                          */
/* ---------------------------------------------------------------------------------------------- */

/** The Exe, from the high moor down past Exford, Bridgetown and Exebridge to Tiverton. */
const EXE = [-460, -70, -300, -20, -130, 33, -60, 64, -5, 92, 22, 120, 30, 150, 30, 178, 48, 205, 75, 226, 105, 262, 128, 300, 142, 350, 140, 412, 152, 520, 172, 700, 190, 940, 205, 1200, 215, 1700]
/** The Barle, joining the Exe below Dulverton. */
const BARLE = [-760, 10, -520, 40, -330, 92, -200, 140, -90, 170, 0, 192, 40, 208, 75, 226]
/** The Bristol Channel shore, west to east, as x and y pairs. */
const COAST = [-1400, -232, -1000, -222, -600, -205, -300, -190, -140, -168, -52, -160, 60, -150, 152, -146, 245, -84, 412, -80, 560, -120, 720, -160, 850, -200, 1000, -238, 1400, -270]

const segmentDistance2 = (x: number, y: number, line: number[]): number => {
    let best = Infinity

    for (let i = 0; i < line.length - 2; i += 2) {
        const ax = line[i]
        const ay = line[i + 1]
        const dx = line[i + 2] - ax
        const dy = line[i + 3] - ay
        const length2 = dx * dx + dy * dy
        let t = length2 > 0 ? ((x - ax) * dx + (y - ay) * dy) / length2 : 0

        t = t < 0 ? 0 : t > 1 ? 1 : t

        const px = ax + dx * t - x
        const py = ay + dy * t - y
        const distance2 = px * px + py * py

        if (distance2 < best) best = distance2
    }

    return best
}

const valley = (x: number, y: number, line: number[], width: number, depth: number): number => {
    const reach2 = width * width * 6.25
    const distance2 = segmentDistance2(x, y, line)

    if (distance2 >= reach2) return 0

    const t = 1 - distance2 / reach2

    return depth * t * t
}

const bump = (x: number, y: number, cx: number, cy: number, radius: number, height: number): number => {
    const dx = (x - cx) / radius
    const dy = (y - cy) / radius
    const f = 1 / (1 + dx * dx + dy * dy)

    return height * f * f
}

const coastAt = (x: number): number => {
    if (x <= COAST[0]) return COAST[1]

    for (let i = 0; i < COAST.length - 2; i += 2) {
        if (x <= COAST[i + 2]) {
            const t = (x - COAST[i]) / (COAST[i + 2] - COAST[i])

            return COAST[i + 1] + (COAST[i + 3] - COAST[i + 1]) * t
        }
    }

    return COAST[COAST.length - 1]
}

/** Height in metres: the moor high in the west, the Vale of Taunton low in the east, the ridge in the middle. */
const heightAt = (x: number, y: number): number => {
    const east = smooth01((x - 260 + 0.25 * y) / 420)
    const south = smooth01((y - 120) / 380)
    const distance = Math.sqrt(x * x + y * y)
    // The vale floor sits between two contour levels, so its gentle noise does not scribble lines across it.
    let h = 40 + 320 * (1 - east) * (1 - 0.55 * south)

    h += bump(x, y, 0, 0, 190, 165)
    h += bump(x, y, -140, -62, 85, 55)
    h += bump(x, y, 230, 10, 170, 60)
    // Rough on the moor, smoother in the vale, and calm right round the ridge so its rings stay clean.
    h += fbm(x, y) * 70 * (0.3 + 0.7 * smooth01(distance / 260)) * (1 - 0.5 * east)
    h -= valley(x, y, EXE, 40, 120)
    h -= valley(x, y, BARLE, 34, 90)
    h = h < 12 ? 12 : h
    h -= 540 * smooth01((coastAt(x) + 45 - y) / 90)

    return h
}

/* ---------------------------------------------------------------------------------------------- */
/* Contours                                                                                         */
/* ---------------------------------------------------------------------------------------------- */

const CELL = 18
const COLS = 147
const ROWS = 144
const GRID_X = -1323
const GRID_Y = -900
/** The shoreline, then every 40 metres from 20 to 500. */
const LEVELS = [0, 20, 60, 100, 140, 180, 220, 260, 300, 340, 380, 420, 460, 500]

/** How far either side of the observatory any screen shape can see at a given latitude on the map. */
const reachAt = (y: number): number => {
    if (y >= -540 && y <= 540) return 1340
    if (y >= -910 && y <= 900) return 960
    if (y <= 1000) return 840
    if (y <= 1130) return 760
    if (y <= 1340) return 610
    return 480
}

/** Path coordinates are written in whole map units (under a pixel on every screen) to keep the markup light. */
const quantise = (value: number): number => Math.round(value)

const formatQ = (value: number): string => String(value)

/** Douglas-Peucker simplification of a flat x, y list. */
const simplify = (points: number[], tolerance: number): number[] => {
    const count = points.length / 2

    if (count <= 2) return points

    const keep = new Uint8Array(count)
    const stack: number[] = [0, count - 1]
    const tolerance2 = tolerance * tolerance

    keep[0] = 1
    keep[count - 1] = 1

    while (stack.length) {
        const last = stack.pop() as number
        const first = stack.pop() as number
        const ax = points[first * 2]
        const ay = points[first * 2 + 1]
        const dx = points[last * 2] - ax
        const dy = points[last * 2 + 1] - ay
        const length2 = dx * dx + dy * dy
        let worst = -1
        let worstDistance = tolerance2

        for (let i = first + 1; i < last; i++) {
            const px = points[i * 2] - ax
            const py = points[i * 2 + 1] - ay
            let distance2: number

            if (length2 > 0) {
                const cross = px * dy - py * dx

                distance2 = (cross * cross) / length2
            }
            else {
                distance2 = px * px + py * py
            }

            if (distance2 > worstDistance) {
                worstDistance = distance2
                worst = i
            }
        }

        if (worst >= 0) {
            keep[worst] = 1
            stack.push(first, worst, worst, last)
        }
    }

    const result: number[] = []

    for (let i = 0; i < count; i++) {
        if (keep[i]) result.push(points[i * 2], points[i * 2 + 1])
    }

    return result
}

const polylineLength = (points: number[]): number => {
    let length = 0

    for (let i = 2; i < points.length; i += 2) {
        const dx = points[i] - points[i - 2]
        const dy = points[i + 1] - points[i - 1]

        length += Math.sqrt(dx * dx + dy * dy)
    }

    return length
}

/** A smooth path through a polyline: quadratic curves through the midpoints, in relative steps without drift. */
const smoothPath = (points: number[], closed: boolean): string => {
    const count = points.length / 2
    const parts: string[] = []
    let cx = 0
    let cy = 0

    const move = (x: number, y: number) => {
        cx = quantise(x)
        cy = quantise(y)
        parts.push(`M${formatQ(cx)} ${formatQ(cy)}`)
    }

    const line = (x: number, y: number) => {
        const qx = quantise(x)
        const qy = quantise(y)

        parts.push(`l${formatQ(qx - cx)} ${formatQ(qy - cy)}`)
        cx = qx
        cy = qy
    }

    const curve = (x1: number, y1: number, x: number, y: number) => {
        const q1x = quantise(x1)
        const q1y = quantise(y1)
        const qx = quantise(x)
        const qy = quantise(y)

        parts.push(`q${formatQ(q1x - cx)} ${formatQ(q1y - cy)} ${formatQ(qx - cx)} ${formatQ(qy - cy)}`)
        cx = qx
        cy = qy
    }

    const px = (i: number) => points[((i % count) + count) % count * 2]
    const py = (i: number) => points[((i % count) + count) % count * 2 + 1]

    if (closed) {
        move((px(-1) + px(0)) / 2, (py(-1) + py(0)) / 2)

        for (let i = 0; i < count; i++) curve(px(i), py(i), (px(i) + px(i + 1)) / 2, (py(i) + py(i + 1)) / 2)

        parts.push('z')
    }
    else {
        move(px(0), py(0))

        if (count === 2) {
            line(px(1), py(1))
        }
        else {
            line((px(0) + px(1)) / 2, (py(0) + py(1)) / 2)

            for (let i = 1; i < count - 1; i++) curve(px(i), py(i), (px(i) + px(i + 1)) / 2, (py(i) + py(i + 1)) / 2)

            line(px(count - 1), py(count - 1))
        }
    }

    return parts.join('')
}

/** When a line starts to trace itself: a slow sweep across the map from west to east. */
const traceTiming = (points: number[], length: number, level: number): { delay: number, duration: number } => {
    let sum = 0

    for (let i = 0; i < points.length; i += 2) sum += points[i]

    const across = smooth01((sum / (points.length / 2) + 950) / 1900)

    return {
        delay: Math.round((0.15 + across * 2.6 + level * 0.035) * 100) / 100,
        duration: Math.round((2.2 + Math.min(2.2, length / 900)) * 100) / 100
    }
}

const buildContours = (): ContourPath[] => {
    const stride = COLS + 1
    const heights = new Float64Array(stride * (ROWS + 1))

    for (let j = 0; j <= ROWS; j++) {
        const y = GRID_Y + j * CELL

        for (let i = 0; i <= COLS; i++) heights[j * stride + i] = heightAt(GRID_X + i * CELL, y)
    }

    const keys = stride * (ROWS + 1) * 2
    const edgeX = new Float64Array(keys)
    const edgeY = new Float64Array(keys)
    const linkA = new Int32Array(keys)
    const linkB = new Int32Array(keys)
    const result: ContourPath[] = []

    LEVELS.forEach((level, levelIndex) => {
        const segA: number[] = []
        const segB: number[] = []

        linkA.fill(-1)
        linkB.fill(-1)

        // Horizontal edge (i, j)-(i + 1, j) and vertical edge (i, j)-(i, j + 1).
        const hKey = (i: number, j: number): number => (j * stride + i) * 2
        const vKey = (i: number, j: number): number => (j * stride + i) * 2 + 1

        const point = (key: number, i: number, j: number, horizontal: boolean): number => {
            const a = heights[j * stride + i]
            const b = horizontal ? heights[j * stride + i + 1] : heights[(j + 1) * stride + i]
            const t = (level - a) / (b - a)

            edgeX[key] = GRID_X + (horizontal ? i + t : i) * CELL
            edgeY[key] = GRID_Y + (horizontal ? j : j + t) * CELL

            return key
        }

        const link = (a: number, b: number) => {
            const index = segA.length

            segA.push(a)
            segB.push(b)

            if (linkA[a] < 0) linkA[a] = index
            else linkB[a] = index

            if (linkA[b] < 0) linkA[b] = index
            else linkB[b] = index
        }

        for (let j = 0; j < ROWS; j++) {
            const reach = reachAt(GRID_Y + (j + 0.5) * CELL)

            for (let i = 0; i < COLS; i++) {
                const centreX = GRID_X + (i + 0.5) * CELL

                if (centreX < -reach || centreX > reach) continue

                const tl = heights[j * stride + i]
                const tr = heights[j * stride + i + 1]
                const br = heights[(j + 1) * stride + i + 1]
                const bl = heights[(j + 1) * stride + i]
                const index = (tl > level ? 8 : 0) | (tr > level ? 4 : 0) | (br > level ? 2 : 0) | (bl > level ? 1 : 0)

                if (index === 0 || index === 15) continue

                const top = () => point(hKey(i, j), i, j, true)
                const bottom = () => point(hKey(i, j + 1), i, j + 1, true)
                const left = () => point(vKey(i, j), i, j, false)
                const right = () => point(vKey(i + 1, j), i + 1, j, false)

                switch (index) {
                    case 1:
                    case 14:
                        link(left(), bottom())
                        break
                    case 2:
                    case 13:
                        link(bottom(), right())
                        break
                    case 3:
                    case 12:
                        link(left(), right())
                        break
                    case 4:
                    case 11:
                        link(top(), right())
                        break
                    case 6:
                    case 9:
                        link(top(), bottom())
                        break
                    case 7:
                    case 8:
                        link(left(), top())
                        break
                    case 5:
                        if ((tl + tr + br + bl) / 4 > level) {
                            link(left(), top())
                            link(bottom(), right())
                        }
                        else {
                            link(top(), right())
                            link(left(), bottom())
                        }
                        break
                    case 10:
                        if ((tl + tr + br + bl) / 4 > level) {
                            link(top(), right())
                            link(left(), bottom())
                        }
                        else {
                            link(left(), top())
                            link(bottom(), right())
                        }
                        break
                    default:
                        break
                }
            }
        }

        // Join the segments into polylines through their shared edge points.
        const used = new Uint8Array(segA.length)
        const other = (segment: number, key: number): number => (segA[segment] === key ? segB[segment] : segA[segment])
        const next = (key: number): number => {
            const a = linkA[key]
            const b = linkB[key]

            if (a >= 0 && !used[a]) return a
            if (b >= 0 && !used[b]) return b

            return -1
        }

        let lineIndex = 0

        for (let start = 0; start < segA.length; start++) {
            if (used[start]) continue

            used[start] = 1

            const forward: number[] = [segA[start], segB[start]]
            let key = segB[start]
            let segment = next(key)

            while (segment >= 0) {
                used[segment] = 1
                key = other(segment, key)
                forward.push(key)
                segment = next(key)
            }

            const backward: number[] = []

            key = segA[start]
            segment = next(key)

            while (segment >= 0) {
                used[segment] = 1
                key = other(segment, key)
                backward.push(key)
                segment = next(key)
            }

            const chain = backward.reverse().concat(forward)
            const closed = chain.length > 3 && chain[0] === chain[chain.length - 1]
            const raw: number[] = []

            for (let c = 0; c < chain.length - (closed ? 1 : 0); c++) raw.push(edgeX[chain[c]], edgeY[chain[c]])

            const gapX = raw[0] - raw[raw.length - 2]
            const gapY = raw[1] - raw[raw.length - 1]
            const length = polylineLength(raw) + (closed ? Math.sqrt(gapX * gapX + gapY * gapY) : 0)

            if (length < (closed ? 70 : 40)) continue

            const simple = simplify(raw, 1.1)

            if (simple.length < 4) continue

            const timing = traceTiming(simple, length, levelIndex)

            result.push({
                key: `${level}-${lineIndex++}`,
                d: smoothPath(simple, closed && simple.length >= 6),
                kind: level === 0 ? 'coast' : (level - 20) % 200 === 80 ? 'index' : 'line',
                delay: timing.delay,
                duration: timing.duration
            })
        }
    })

    return result
}

let contours: ContourPath[] | null = null

/** Every contour on the map, built once and shared by the server and the browser. */
export const getContours = (): ContourPath[] => {
    if (!contours) contours = buildContours()

    return contours
}

/* ---------------------------------------------------------------------------------------------- */
/* Rivers, roads and the route                                                                      */
/* ---------------------------------------------------------------------------------------------- */

/** Samples a Catmull-Rom curve through the control points into a dense flat x, y list. */
const sampleCurve = (control: number[], steps: number): number[] => {
    const count = control.length / 2
    const out: number[] = [control[0], control[1]]
    const at = (i: number, axis: number) => control[Math.max(0, Math.min(count - 1, i)) * 2 + axis]

    for (let i = 0; i < count - 1; i++) {
        for (let s = 1; s <= steps; s++) {
            const t = s / steps
            const t2 = t * t
            const t3 = t2 * t

            for (let axis = 0; axis < 2; axis++) {
                const p0 = at(i - 1, axis)
                const p1 = at(i, axis)
                const p2 = at(i + 1, axis)
                const p3 = at(i + 2, axis)

                out.push(0.5 * ((2 * p1) + (-p0 + p2) * t + (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 + (-p0 + 3 * p1 - 3 * p2 + p3) * t3))
            }
        }
    }

    return out
}

const linePath = (points: number[]): string => {
    const parts: string[] = []
    let cx = quantise(points[0])
    let cy = quantise(points[1])

    parts.push(`M${formatQ(cx)} ${formatQ(cy)}`)

    for (let i = 2; i < points.length; i += 2) {
        const qx = quantise(points[i])
        const qy = quantise(points[i + 1])

        parts.push(`l${formatQ(qx - cx)} ${formatQ(qy - cy)}`)
        cx = qx
        cy = qy
    }

    return parts.join('')
}

const mapLine = (key: string, control: number[], delay: number): MapLine => {
    const points = simplify(sampleCurve(control, 8), 0.5)

    return { key, d: linePath(points), delay, duration: Math.round((2.4 + Math.min(2.4, polylineLength(points) / 700)) * 100) / 100 }
}

export const RIVERS: MapLine[] = [
    mapLine('exe', EXE, 1.2),
    mapLine('barle', BARLE, 1.5)
]

export const ROADS: (MapLine & { major: boolean })[] = [
    { ...mapLine('m5', [1000, 40, 900, 140, 807, 240, 700, 270, 592, 304, 480, 340, 362, 380, 300, 430, 240, 530, 196, 680, 170, 900, 150, 1200, 120, 1700], 1.8), major: true },
    { ...mapLine('a361', [362, 380, 300, 388, 230, 398, 150, 404, 60, 430, -80, 452, -260, 470, -520, 480, -900, 470, -1340, 455], 2), major: false },
    { ...mapLine('a396', [140, 412, 141, 382, 147, 352, 136, 322, 122, 294, 105, 266, 88, 244, 74, 226, 58, 212, 44, 196, 38, 176, 42, 156, 36, 134, 40, 112, 47, 90, 43, 68, 46, 48, 35, 24, 52, -10, 80, -50, 115, -95, 152, -132], 2.2), major: false },
    { ...mapLine('a39', [1000, -165, 850, -128, 720, -96, 560, -66, 412, -42, 245, -50, 152, -128, 40, -138, -52, -146, -200, -164, -500, -188, -1000, -205, -1340, -214], 2.4), major: false },
    { ...mapLine('a358', [807, 240, 720, 160, 620, 70, 520, -10, 430, -64], 2.5), major: false },
    { ...mapLine('b3224', [35, 24, -40, 30, -130, 33, -260, 42, -420, 30, -700, 12, -1000, 0, -1340, -6], 2.6), major: false }
]

/** The drive: off the M5 at junction 27, west on the A361, north on the A396 through Dulverton, then up Hollowmoor Lane. */
const ROUTE_LEGS: { road: Road, points: number[], pace: number }[] = [
    { road: 'A361', points: [362, 375, 330, 383, 288, 390, 240, 396, 196, 401, 150, 404], pace: 0.55 },
    { road: 'A396', points: [150, 404, 141, 382, 147, 352, 136, 322, 122, 294, 105, 266, 88, 244, 74, 226, 58, 212, 44, 196, 38, 176, 42, 156, 36, 134, 40, 112, 47, 90, 43, 68, 46, 48, 35, 24], pace: 1 },
    { road: 'Hollowmoor Lane', points: [35, 24, 25, 19, 16, 12, 8, 6, 0, 0], pace: 1.6 }
]

type RouteData = {
    d: string
    x: Float64Array
    y: Float64Array
    distance: Float64Array
    time: Float64Array
    road: Road[]
    length: number
}

const buildRoute = (): RouteData => {
    const xs: number[] = []
    const ys: number[] = []
    const roads: Road[] = []

    ROUTE_LEGS.forEach((leg, legIndex) => {
        const points = sampleCurve(leg.points, 10)

        for (let i = legIndex === 0 ? 0 : 2; i < points.length; i += 2) {
            xs.push(points[i])
            ys.push(points[i + 1])
            roads.push(leg.road)
        }
    })

    const count = xs.length
    const distance = new Float64Array(count)
    const time = new Float64Array(count)

    for (let i = 1; i < count; i++) {
        const dx = xs[i] - xs[i - 1]
        const dy = ys[i] - ys[i - 1]
        const step = Math.sqrt(dx * dx + dy * dy)
        const pace = ROUTE_LEGS.find(leg => leg.road === roads[i])?.pace ?? 1

        distance[i] = distance[i - 1] + step
        time[i] = time[i - 1] + step * pace
    }

    const flat: number[] = []

    for (let i = 0; i < count; i++) flat.push(xs[i], ys[i])

    return {
        d: linePath(flat),
        x: Float64Array.from(xs),
        y: Float64Array.from(ys),
        distance,
        time,
        road: roads,
        length: distance[count - 1]
    }
}

const ROUTE = buildRoute()

export const ROUTE_PATH = ROUTE.d

/** Where the drive has reached after a share of its length, and how many minutes it has taken. Writes into `out`. */
export const routeAt = (share: number, out: RouteSample): RouteSample => {
    const { x, y, distance, time, road, length } = ROUTE
    const target = (share < 0 ? 0 : share > 1 ? 1 : share) * length
    let low = 0
    let high = distance.length - 1

    while (high - low > 1) {
        const middle = (low + high) >> 1

        if (distance[middle] < target) low = middle
        else high = middle
    }

    const span = distance[high] - distance[low]
    const t = span > 0 ? (target - distance[low]) / span : 0

    out.x = x[low] + (x[high] - x[low]) * t
    out.y = y[low] + (y[high] - y[low]) * t
    out.minutes = ((time[low] + (time[high] - time[low]) * t) / time[time.length - 1]) * TRAVEL_MINUTES
    out.road = road[high]

    return out
}

/* ---------------------------------------------------------------------------------------------- */
/* Framing                                                                                          */
/* ---------------------------------------------------------------------------------------------- */

/**
 * The part of the map a screen of a given shape shows. The observatory stays centred across the width; landscape
 * screens keep it in the middle with Taunton at the right-hand edge, tall screens zoom in and lift it into the
 * upper third so the drive sits above the text.
 */
export const viewForAspect = (aspect: number): MapView => {
    const a = Math.min(2.6, Math.max(0.35, aspect))

    if (a >= 1.05) {
        const w = Math.max(1834, 1000 * a)
        const h = w / a

        return { x: -w / 2, y: -h / 2, w, h }
    }

    const w = Math.max(820, 1834 * Math.pow(a / 1.05, 0.85))
    const h = w / a
    const above = 0.3 + 0.2 * smooth01((a - 0.6) / 0.45)

    return { x: -w / 2, y: -h * above, w, h }
}

/** The view for a screen in pixels, cropped to its exact shape so the map and any overlays line up. */
export const viewFor = (width: number, height: number): MapView => {
    const aspect = width / Math.max(1, height)
    const view = viewForAspect(aspect)

    if (view.w / view.h > aspect) {
        const w = view.h * aspect

        return { x: view.x + (view.w - w) / 2, y: view.y, w, h: view.h }
    }

    if (view.w / view.h < aspect) {
        const h = view.w / aspect

        return { x: view.x, y: view.y + (view.h - h) / 2, w: view.w, h }
    }

    return view
}

/** The server renders for a 16:9 screen; the browser reframes on mount. */
export const DEFAULT_VIEW: MapView = { x: -917, y: -515.8125, w: 1834, h: 1031.625 }

/* ---------------------------------------------------------------------------------------------- */
/* Stars                                                                                            */
/* ---------------------------------------------------------------------------------------------- */

export type MapStar = {
    x: number
    y: number
    /** Radius in screen pixels. */
    r: number
    opacity: number
}

const mulberry32 = (seed: number): (() => number) => {
    let state = seed >>> 0

    return () => {
        state = (state + 0x6d2b79f5) >>> 0

        let t = state

        t = Math.imul(t ^ (t >>> 15), t | 1)
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61)

        return ((t ^ (t >>> 14)) >>> 0) / 4294967296
    }
}

/** Four bands of stars, brightest first, scattered across the view: more of them come out as the sky darkens. */
export const starBands = (view: MapView, widthPx: number, heightPx: number): MapStar[][] => {
    const random = mulberry32(9071)
    const count = Math.round(Math.min(640, Math.max(180, (widthPx * heightPx) / 2400)))
    const unitsPerPixel = view.w / Math.max(1, widthPx)
    const bands: MapStar[][] = [[], [], [], []]
    const shares = [0.1, 0.3, 0.6, 1]
    const radii = [1.35, 1.05, 0.82, 0.62]
    const clear = 34 * unitsPerPixel

    for (let i = 0; i < count; i++) {
        const x = view.x + random() * view.w
        const y = view.y + random() * view.h
        const order = i / count
        const band = shares.findIndex(share => order < share)
        const twinkle = random()

        if (x * x + y * y < clear * clear) continue

        bands[band < 0 ? 3 : band].push({
            x,
            y,
            r: radii[band < 0 ? 3 : band] * (0.85 + twinkle * 0.3),
            opacity: 0.55 + random() * 0.45
        })
    }

    return bands
}

/** Stars visible overhead as the darkness deepens from 0 (Taunton) to 1 (the ridge). */
export const starsVisible = (darkness: number): number => {
    const d = darkness < 0 ? 0 : darkness > 1 ? 1 : darkness
    const count = STARS_IN_TOWN * Math.pow(STARS_ON_THE_RIDGE / STARS_IN_TOWN, Math.pow(d, 1.1))

    return count < 1000 ? Math.round(count / 10) * 10 : Math.round(count / 50) * 50
}
